import React, { memo, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Used for errors emmitted from react-simple-maps
import { ErrorBoundary } from "react-error-boundary";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Tooltip } from "react-tooltip";

import statesLegalizationData from "./statesLegalizationData.json";
// TopoJson data: https://github.com/topojson/topojson
import usTopographyData from "./usTopographyData.json";

type TooltipInfoType = {
  adult?: boolean;
  decriminalized?: boolean;
  status?: string;
  medical?: boolean;
  name?: string;
};

const legalizationColorMapping: { [key: string]: string } = {
  "adult use": "var(--color-green)",
  illegal: "var(--color-light-grey)",
  medical: "var(--color-yellow)",
};

const getCountryStateData = (geoId: string) =>
  statesLegalizationData.find((s) => s.val === geoId);

const Map: React.FC<{
  setTooltipContent: (content?: TooltipInfoType) => void;
}> = memo(({ setTooltipContent }) => (
  <div data-tip="">
    <ComposableMap projection="geoAlbersUsa">
      <Geographies geography={usTopographyData}>
        {({ geographies }) =>
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
          geographies.map((geo: any) => {
            const data = getCountryStateData(geo.id);
            const fillColor =
              legalizationColorMapping[data?.status || "illegal"];
            return (
              <Geography
                key={geo.rsmKey}
                stroke="#FFF"
                geography={geo}
                fill={fillColor}
                onMouseEnter={() =>
                  setTooltipContent({
                    adult: data?.adult,
                    decriminalized: data?.decriminalized,
                    medical: data?.medical,
                    name: geo.properties.name,
                    status: data?.status,
                  })
                }
                onMouseLeave={() => setTooltipContent()}
              />
            );
          })
        }
      </Geographies>
    </ComposableMap>
  </div>
));

const TooltipInfo: React.FC<{ info: TooltipInfoType }> = ({ info }) => {
  const { adult, decriminalized, status, medical, name } = info;
  const formatStatus = (status?: boolean) =>
    status === true ? "Yes" : status === false ? "No" : "N/A";

  return (
    <>
      <p className="text-green mb-2 font-bold text-sm">{name}</p>
      <ul className="capitalize">
        <li className="mb-1 text-xs">
          <span className="font-bold">Legalization status</span> - {status}
        </li>
        <li className="mb-1 text-xs">
          <span className="font-bold">Medical</span> - {formatStatus(medical)}
        </li>
        <li className="mb-1 text-xs">
          <span className="font-bold">Recreational</span> -{" "}
          {formatStatus(adult)}
        </li>
        <li className="mb-1 text-xs">
          <span className="font-bold">Decriminalized</span> -{" "}
          {formatStatus(decriminalized)}
        </li>
      </ul>
    </>
  );
};

const LegalizationMap = () => {
  const [content, setContent] = useState<TooltipInfoType>();

  return (
    <ErrorBoundary fallback={<></>}>
      <div className="interactive-map">
        <ul className="flex">
          {Object.keys(legalizationColorMapping).map((key, i) => (
            <li
              className="flex items-center mr-lg"
              key={`legalization-map-key-${i}`}
            >
              <span
                className="rounded h-2 w-2 block mr-sm"
                style={{ backgroundColor: legalizationColorMapping[key] }}
              />
              <span className="text-sm capitalize">{key}</span>
            </li>
          ))}
        </ul>

        <Map setTooltipContent={setContent} />

        <Tooltip className="shadow-md bg-white text-default">
          {content && <TooltipInfo info={content} />}
        </Tooltip>
      </div>
    </ErrorBoundary>
  );
};

export default LegalizationMap;
